import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SeatChangeRequest } from '../types';
import '../styles/SeatChangeRequest.css';

const SeatChangeRequestComponent: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [requestedSeat, setRequestedSeat] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [errors, setErrors] = useState<{ requestedSeat?: string; reason?: string }>({});
  const [requests, setRequests] = useState<SeatChangeRequest[]>([
    {
      id: '1',
      studentId: user?.studentId || 'STU001',
      currentSeat: 15,
      requestedSeat: 20,
      reason: 'Need to sit closer to the window',
      status: 'pending',
      requestedDate: '2024-01-20'
    }
  ]);

  const currentSeat = 15; // Mock current seat - in real app, get from API
  const totalSeats = 36;

  const validateForm = (): boolean => {
    const newErrors: { requestedSeat?: string; reason?: string } = {};

    if (!requestedSeat || requestedSeat < 1 || requestedSeat > totalSeats) {
      newErrors.requestedSeat = `Please enter a valid seat number (1-${totalSeats})`;
    } else if (requestedSeat === currentSeat) {
      newErrors.requestedSeat = 'You cannot request your current seat';
    }

    if (!reason.trim()) {
      newErrors.reason = 'Please provide a reason for the seat change';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const newRequest: SeatChangeRequest = {
        id: String(requests.length + 1),
        studentId: user?.studentId || 'STU001',
        currentSeat,
        requestedSeat,
        reason: reason.trim(),
        status: 'pending',
        requestedDate: new Date().toISOString().split('T')[0]
      };

      setRequests([newRequest, ...requests]);
      setRequestedSeat(0);
      setReason('');
      setErrors({});
      showToast('Seat change request submitted successfully!', 'success');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClass = status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'pending';
    return <span className={`status-badge ${statusClass}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  return (
    <div className="seat-change-request">
      <div className="request-header">
        <h2>Request for Change Seat</h2>
        <p>Submit a request to change your current seat assignment</p>
      </div>

      <div className="request-content">
        <div className="current-seat-info-card">
          <div className="info-item">
            <label>Current Seat</label>
            <div className="seat-display">{currentSeat}</div>
          </div>
          <div className="info-item">
            <label>Student ID</label>
            <div className="student-id-display">{user?.studentId || 'STU001'}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="request-form">
          <div className="form-group">
            <label htmlFor="requested-seat">Requested Seat Number</label>
            <input
              type="number"
              id="requested-seat"
              min="1"
              max={totalSeats}
              value={requestedSeat || ''}
              onChange={(e) => {
                setRequestedSeat(parseInt(e.target.value) || 0);
                if (errors.requestedSeat) {
                  setErrors(prev => ({ ...prev, requestedSeat: undefined }));
                }
              }}
              className={`form-input ${errors.requestedSeat ? 'error' : ''}`}
              placeholder={`Enter seat number (1-${totalSeats})`}
            />
            {errors.requestedSeat && (
              <span className="error-message">{errors.requestedSeat}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="reason">Reason for Change</label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) {
                  setErrors(prev => ({ ...prev, reason: undefined }));
                }
              }}
              className={`form-textarea ${errors.reason ? 'error' : ''}`}
              placeholder="Please provide a detailed reason for requesting this seat change..."
              rows={5}
            />
            {errors.reason && (
              <span className="error-message">{errors.reason}</span>
            )}
          </div>

          <button type="submit" className="submit-button">
            Submit Request
          </button>
        </form>

        <div className="requests-history">
          <h3>Request History</h3>
          {requests.length === 0 ? (
            <div className="no-requests">
              <p>No requests submitted yet</p>
            </div>
          ) : (
            <div className="requests-list">
              {requests.map((request) => (
                <div key={request.id} className="request-card">
                  <div className="request-header-row">
                    <div className="request-info">
                      <span className="request-id">Request #{request.id}</span>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="request-date">
                      {request.requestedDate && new Date(request.requestedDate).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="request-details">
                    <div className="detail-item">
                      <span className="detail-label">From:</span>
                      <span className="detail-value">Seat {request.currentSeat}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">To:</span>
                      <span className="detail-value">Seat {request.requestedSeat}</span>
                    </div>
                  </div>
                  <div className="request-reason">
                    <span className="reason-label">Reason:</span>
                    <p>{request.reason}</p>
                  </div>
                  {request.reviewedDate && (
                    <div className="request-review-date">
                      Reviewed on: {new Date(request.reviewedDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeatChangeRequestComponent;

